import { api } from './client';

// All endpoints require the `admin:users:manage` capability server-side; the
// UI hides the Admin tab unless /api/v1/me reports that permission.

export interface AdminUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  mattermostUserId: string | null;
  status: 'active' | 'invited' | 'disabled' | 'archived';
  spaceIds: number[];
  roleKeys: string[];
}

export interface AdminSpace {
  id: number;
  slug: string;
  name: string;
}

export interface AdminRole {
  id: number;
  key: string;
  name: string;
  scope: 'tenant' | 'space';
}

export interface CreateUserResult {
  userId: number;
  email: string;
  created: boolean;
  /** present only when a brand-new login was created (share it once) */
  tempPassword?: string;
}

export function listAdminUsers(): Promise<AdminUser[]> {
  return api.get<AdminUser[]>('/api/v1/admin/users');
}

export function listAdminSpaces(): Promise<AdminSpace[]> {
  return api.get<AdminSpace[]>('/api/v1/admin/spaces');
}

export function listAdminRoles(): Promise<AdminRole[]> {
  return api.get<AdminRole[]>('/api/v1/admin/roles');
}

export function createAdminUser(body: {
  email: string;
  firstName?: string;
  lastName?: string;
  mattermostUserId?: string;
  password?: string;
}): Promise<CreateUserResult> {
  return api.post<CreateUserResult>('/api/v1/admin/users', body);
}

export function setAdminUserAccess(
  userId: number,
  body: { spaceIds: number[]; roleKey: string },
): Promise<{ userId: number; spaceIds: number[]; roleKey: string }> {
  return api.put(`/api/v1/admin/users/${userId}/access`, body);
}

export function resetAdminUserPassword(
  userId: number,
  newPassword: string,
): Promise<{ updated: boolean }> {
  return api.put<{ updated: boolean }>(`/api/v1/admin/users/${userId}/password`, { newPassword });
}

export function resetAdminUserMfa(userId: number): Promise<{ reset: boolean }> {
  return api.post<{ reset: boolean }>(`/api/v1/admin/users/${userId}/mfa/reset`);
}

// Smazání (soft-delete) uživatele - odebere ze seznamu + zruší přístup.
export function deleteAdminUser(userId: number): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>(`/api/v1/admin/users/${userId}`);
}

export type AccessLogAction = 'list' | 'detail' | 'write' | 'export';

export interface AccessLogRow {
  id: number;
  createdAt: string;
  userId: number;
  userName: string;
  email: string;
  spaceId: number | null;
  moduleKey: string;
  action: AccessLogAction;
  method: string;
  path: string;
  entityId: string | null;
  statusCode: number;
  ip: string | null;
}

export interface AccessSummaryRow {
  userId: number;
  userName: string;
  email: string;
  total: number;
  details: number;
  writes: number;
  modules: string;
}

export interface AccessLogStats {
  rows: number;
  bytes: number;
  oldestAt: string | null;
  retentionDays: number;
}

export interface AccessLogFilters {
  days: number;
  month?: string | null;
  userId?: number | null;
  module?: string | null;
  writes?: boolean;
}

function accessLogQuery(filters: AccessLogFilters, format?: 'csv'): string {
  const query = filters.month ? new URLSearchParams({ month: filters.month }) : new URLSearchParams({ days: String(filters.days) });
  if (filters.userId) query.set('userId', String(filters.userId));
  if (filters.module) query.set('module', filters.module);
  if (filters.writes) query.set('writes', '1');
  if (format) query.set('format', format);
  return query.toString();
}

export function listAccessLog(filters: AccessLogFilters): Promise<{ rows: AccessLogRow[] }> {
  return api.get(`/api/v1/admin/access-log?${accessLogQuery(filters)}`);
}

export function accessLogCsvUrl(filters: AccessLogFilters): string {
  return `/api/v1/admin/access-log?${accessLogQuery(filters, 'csv')}`;
}

export function accessLogSummary(filters: Pick<AccessLogFilters, 'days' | 'month'>): Promise<{ rows: AccessSummaryRow[]; modules: string[]; stats: AccessLogStats }> {
  return api.get(`/api/v1/admin/access-log/summary?${accessLogQuery({ days: filters.days, month: filters.month })}`);
}

export function sendAccessSummaryNow(): Promise<{ sent: boolean }> {
  return api.post('/api/v1/admin/access-log/summary/send');
}

export type AccessLevel = 'read' | 'write';

export interface ModuleCatalogItem {
  key: string;
  label: string;
}
export interface SpaceModuleCatalog {
  spaceId: number;
  slug: string;
  name: string;
  modules: ModuleCatalogItem[];
}

export function listModuleCatalog(): Promise<SpaceModuleCatalog[]> {
  return api.get<SpaceModuleCatalog[]>('/api/v1/admin/module-catalog');
}

export function getAdminUserAccess(userId: number): Promise<Record<number, Record<string, AccessLevel>>> {
  return api.get(`/api/v1/admin/users/${userId}/access`);
}

// Role uživatele (1:1 prod): user = matice čtení/editace, admin = plný admin
// vybraných Spaců (space_admin), super_admin = celé P1 (tenant_admin).
export type UserRole = 'user' | 'admin' | 'super_admin';

// Uloží přístup uživatele dle role:
//  - super_admin → access se ignoruje (celé P1)
//  - admin       → access = vybrané Spacy (modules prázdné, plný přístup z role)
//  - user        → access = matice modulů (read/write)
export function saveAdminUserAccess(
  userId: number,
  role: UserRole,
  access: { spaceId: number; modules: Record<string, AccessLevel> }[],
): Promise<{ userId: number; spaceIds: number[]; roleKey: string }> {
  return api.put(`/api/v1/admin/users/${userId}/access`, { role, access });
}

export interface MmValidateResult {
  ok: boolean;
  server: string;
  userId?: string;
  username?: string;
  displayName?: string;
  error?: string;
}
export function validateMm(q: string): Promise<MmValidateResult> {
  return api.get<MmValidateResult>(`/api/v1/admin/mm/validate?q=${encodeURIComponent(q)}`);
}
